import { TextureData } from "../../types/types";
import { Padding } from "../../utils/Padding";
import { RectSize } from "../../utils/RectSize";
import { LoaderFileKeys } from "../constant/AppConstants";
import { OptionsDataManager } from "../managers/OptionsDataManager";
import { AppSettings } from "../managers/AppSettings";
import { AppTimings } from "../data/AppConstants";


/**
 * BASE CLASS. DO NOT USE ON ITS OWN! (size and size calculation is based on content which doesnt exist!)
 * Bubble NineSlice white background with a tip/triangle.
 * No additional content. Tip's sharp edge (without effects) is anchored at (0,0).
 * Respects @see this.origin by snapping triangle 'tip' to 0,0 within this container and aligning rest of content from there based on origin.
 * 
 * @usage
    DO NOT USE STANDALONE, use subclasses for ex: @see TextBubble
 * 
 * NOTE: you must call SetOrigin (with updateView=true) with or SetOrigin + UpdateView, else positions of children will be off.
 */
export class BubbleBase
    extends Phaser.GameObjects.Container {
    public bg: Phaser.GameObjects.NineSlice;
    public bgEffectsPadding: Padding;
    /**
     * Anchor/origin of this tip is anchored to (0,0)
     */
    public bgTip: Phaser.GameObjects.Image;
    public isShowing: boolean;

    protected baseScale: number;
    /**
     * Total pixel size, includes @see contentSize, plus texture-embedded effects like bg dropshadow.
     */
    protected totalSize: RectSize;
    /**
     * Content size includes size of content and any spacing/padding.
     * Visually, this size refers to the opaque part of bg (solid bubble).
     */
    protected contentSize: RectSize;
    /**
     * Internal content origin - rearranges in position children of this container around (0,0) 
     * NOTE: cant use originX/originY, because it is internal to Phaser.
     */
    protected origin: Phaser.Geom.Point;
    protected bgTextureData: TextureData;
    protected bgSideWidth: number;
    protected tipTextureData: TextureData;

    /**
     * 
     * @param bgEffectsPadding Pixel size of extrusion offects like dropshadow (that are part of texture!)
     */
    public Init(
        bgTextureData: TextureData,
        /**
         * For NineSlice side size
         */
        bgSideWidth: number,
        tipTextureData: TextureData,
        bgEffectsPadding?: Padding
    ): void {
        this.bgTextureData = bgTextureData;
        this.bgSideWidth = bgSideWidth;
        this.tipTextureData = tipTextureData;
        this.bgEffectsPadding = bgEffectsPadding ?? new Padding(9);

        this.contentSize = new RectSize();
        this.totalSize = new RectSize();
        this.contentSize = new RectSize();
        this.origin = new Phaser.Geom.Point();
    }

    public Build(): void {
        // console.log(this.bgTextureData.texture, this.bgTextureData.frame, this.bgSideWidth);
        this.bg = this.scene.make.nineslice({
            key: this.bgTextureData.texture,
            frame: this.bgTextureData.frame,
            leftWidth: this.bgSideWidth,
            rightWidth: this.bgSideWidth,
            topHeight: this.bgSideWidth,
            bottomHeight: this.bgSideWidth,
            origin: 0.5,
        });
        this.add(this.bg);

        this.bgTip = this.scene.make.image({
            key: LoaderFileKeys.ui,
            frame: "speechBubble-triangle.png",
        });
        this.add(this.bgTip);

        // DEBUG origin
        // this.add(this.scene.make.image({key:LoaderFileKeys.ui, frame:"btn-round2-bg-over.png"}).setScale(0.2));
    }

    public Show(animated: boolean = true, onComplete: Function = undefined): void {
        // console.log("TablePopupWindow::Show; animated=", animated);

        this.isShowing = true;

        // kill tweens
        this.scene.tweens.killTweensOf(this);

        // reset
        this.scale = 0;
        this.visible = true;

        // if animate
        let genericShowDuration = OptionsDataManager.GetTiming(AppTimings.hintBubbleShowDuration);
        // genericShowDuration = 2000;
        if (animated) {
            this.scene.tweens.add({
                targets: this,
                // scale: this.baseScale,
                scale: 1,
                duration: genericShowDuration,
                ease: AppSettings.ease.genericShowHideEaseNoOvershoot,
                onComplete: onComplete?.(),
            })
        }
        else {
            //
            // this.scale = this.baseScale;
            this.scale = 1;
            // 
            onComplete?.();
        }
    }

    /**
     * NOTE: scale=1 reset when invis so that TEXT set while hidden can be centered properly.
     * @param animated 
     */
    public Hide(animated: boolean = true, onComplete: Function = undefined): void {
        // console.log("PopupBase::Hide; animated=", animated);

        this.isShowing = false;

        // kill tweens
        this.scene.tweens.killTweensOf(this);

        // ref

        // if animate
        let genericHideDuration = OptionsDataManager.GetTiming(AppTimings.hintBubbleHideDuration);
        // genericHideDuration = 2000;
        if (animated) {
            this.scene.tweens.add({
                targets: this,
                // alpha: ,
                scale: 0,
                duration: genericHideDuration,
                ease: AppSettings.ease.genericShowHideEaseNoOvershoot,
                onComplete: () => {
                    // set scale to baseScale to prevent miscalculation during SetText with scale 0;
                    this.visible = false;
                    // this.setScale(this.baseScale);
                    this.setScale(1);
                    // 
                    onComplete?.();
                }
            })
        }
        else {
            // 
            this.visible = false;
            // this.scale = this.baseScale;
            this.scale = 1;
            // 
            onComplete?.();
        }
    }

    /**
     * NOTE: there are no base calculations for this, because bg is not resized properly (no content) - bg indeed resizes based on size of content, not other way around.
     * @returns size of content (excludes effects like bg dropshadow (embedded in texture))
     */
    public GetTotalSize(): RectSize {
        return this.totalSize;
    }

    public GetContentSize(): RectSize {
        return this.contentSize;
    }

    /**
     * originX: 0 -> inner content anchored to the left (0,0), growing to the right
     * originX: 1 -> inner content aligned to the right (0,0), growing to the left
     * originY: 0 -> inner content anchored to the top (0,0), growing to the bottom
     * originY: 1 -> inner content aligned to the bottom (0,0), growing to the top
     */
    public SetOrigin(originX: number, originY: number, updateView: boolean = false): void {
        this.origin.setTo(originX, originY);

        // update assets origin
        if (this.origin.x == 0) {
            this.bgTip.angle = 180;
            // EDIT: if you do this, then everything you change size, youll have to recalc origin (BAD)
            // this.bg.originX = this.bgEffectsPadding.left / this.bg.width;
        }
        else
            if (this.origin.x == 1) {
                this.bgTip.angle = 0;
                // this.bg.originX = (this.bg.width - this.bgEffectsPadding.right) / this.bg.width;
            }
        if (this.origin.y == 0) {
            this.bgTip.angle = 270;
            // this.bg.originY = this.bgEffectsPadding.top / this.bg.height; 
        }
        else
            if (this.origin.y == 1) {
                this.bgTip.angle = 90;
                // this.bg.originY = (this.bg.height - this.bgEffectsPadding.bottom) / this.bg.height; 
            }

        if (updateView) {
            this.UpdateView();
        }
    }

    public UpdateView(): void {
        // override in subclass
    }

    /**
     * Snap bg to tip, respecting origin
     * Correctiong: to avoid seams, we use +/-1px overlap between bg and tip.
     * NOTE: called from subclasse's child
     */
    protected SnapBgToTip(): void {
        // angle/orientation doesnt change default displayWidth or originX (its just y-axis now)
        // console.log(this.bgTip.originX, this.bgTip.originY, this.bgTip.displayWidth, this.bgTip.displayHeight);
        // horizontal
        if (this.origin.x == 0) {
            this.bg.x = this.bgTip.displayWidth * this.bgTip.originX - 1 + this.contentSize.width * 0.5;
        }
        else
            if (this.origin.x == 1) {
                this.bg.x = -this.bgTip.displayWidth * this.bgTip.originX + 1 - this.contentSize.width * 0.5;
            }
        // vertical
        if (this.origin.y == 0) {
            this.bg.y = this.bgTip.displayWidth * this.bgTip.originX - 1 + this.contentSize.height * 0.5;
        }
        else if (this.origin.y == 1) {
            this.bg.y = -this.bgTip.displayWidth * this.bgTip.originX + 1 - this.contentSize.height * 0.5;
        }
    }
}